import i18next from 'i18next';
import { parsePhoneNumber } from 'libphonenumber-js';

function monthNbrToText(nbr) {
    switch (nbr) {
        case 0:
            return '';
        case 1:
            return i18next.t('month.january') ? i18next.t('month.january') : 'month.january';
        case 2:
            return i18next.t('month.february') ? i18next.t('month.february') : 'month.february';
        case 3:
            return i18next.t('month.march') ? i18next.t('month.march') : 'month.march';
        case 4:
            return i18next.t('month.april') ? i18next.t('month.april') : 'month.april';
        case 5:
            return i18next.t('month.may') ? i18next.t('month.may') : 'month.may';
        case 6:
            return i18next.t('month.june') ? i18next.t('month.june') : 'month.june';
        case 7:
            return i18next.t('month.july') ? i18next.t('month.july') : 'month.july';
        case 8:
            return i18next.t('month.august') ? i18next.t('month.august') : 'month.august';
        case 9:
            return i18next.t('month.september') ? i18next.t('month.september') : 'month.september';
        case 10:
            return i18next.t('month.october') ? i18next.t('month.october') : 'month.october';
        case 11:
            return i18next.t('month.november') ? i18next.t('month.november') : 'month.november';
        case 12:
            return i18next.t('month.december') ? i18next.t('month.december') : 'month.december';
        default:
            throw new Error('invalid month number');
    }
}

export const monthNumberToText = (nbr) => {
    return monthNbrToText(nbr);
};

export const monthNumberListToTextList = (monthArr) => {
    if (monthArr && monthArr.length > 0) {
        let monthTxtArr = [];
        for (let mNbr of monthArr) {
            monthTxtArr.push(monthNumberToText(mNbr));
        }
        return monthTxtArr;
    }
    return [];
};

export const verifyEmailAddress = (email) => {
    const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return regex.test(email);
};

export const verifyPasswordStrength = (password) => {
    var passwordStrength = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})');
    return passwordStrength.test(password);
};

export const pseudoRandomPasswordGenerate = (upperCase, lowerCase, numbers) => {
    var chars = ['ABCDEFGHIJKLMNOPQRSTUVWXYZ', 'abcdefghijklmnopqrstuvwxyz', '0123456789'];
    return [upperCase, lowerCase, numbers]
        .map(function (len, i) {
            return Array(len)
                .fill(chars[i])
                .map(function (x) {
                    return x[Math.floor(Math.random() * x.length)];
                })
                .join('');
        })
        .concat()
        .join('')
        .split('')
        .sort(function () {
            return 0.5 - Math.random();
        })
        .join('');
};

export const normalizePhonenumber = (phonenumber, defaultRegion = 'FI') => {
    if (!phonenumber) return null;
    try {
        const parsed = parsePhoneNumber(phonenumber, defaultRegion);
        if (!parsed.isPossible()) return null;
        return parsed.number; // E.164
    } catch {
        return null;
    }
};

export const verifyPhonenumber = (phonenumber) => {
    return !!normalizePhonenumber(phonenumber);
};

export const parseInteger = (value) => {
    let output = parseInt(value);
    if (typeof output !== 'number') {
        return 0;
    }
    return output;
};

//note: if string starts with number, parser return decimals before first letter
export const parseDecimal = (value) => {
    let output = parseFloat(value);
    if (isNaN(output) || typeof output !== 'number') {
        return 0;
    }
    return output;
};

const prepareForCompare = (o) => {
    if (Array.isArray(o)) {
        let arr = [];
        for (let i in o) {
            arr[i] = prepareForCompare(o[i]);
        }
        return arr;
    } else if (typeof o == 'number') {
        return o.toFixed(2);
    } else if (typeof o == 'string') {
        if (o === '0') {
            return '0.00';
        }
        return o;
    } else if (typeof o != 'object') {
        return o;
    }
    let obj = { ...o };
    let removeKeys = [];
    let keys = Object.keys(obj);
    for (let k in keys) {
        let key = keys[k];
        let item = obj[key];
        if (item === null) {
            removeKeys.push(key);
            continue;
        } else {
            item = prepareForCompare(item);
        }
        obj[key] = item;
    }
    for (let i in removeKeys) {
        delete obj[removeKeys[i]];
    }
    return obj;
};

export const objectsDiffer = (objectA, objectB) => {
    return JSON.stringify(prepareForCompare(objectA)) !== JSON.stringify(prepareForCompare(objectB));
};

// Contracts are bound to a single tenant (see dbupdater/scripts/20261018-bind-contracts-to-tenant.sql) --
// a customer/location may only use contracts belonging to its own tenant. The ParameterSelector for
// ContractNumber is driven off the flat parameters['ContractNumber'] catalog (id/value pairs with no
// tenant info of its own), so filter that list against contractsById to hide the other tenants' contracts.
// Returns a copy of `parameters` with ContractNumber replaced by the filtered list; unrelated categories
// are left untouched. id === 1 ("ei sopimustunnusta") is always kept since it's never a real contract row.
export const filterContractParametersByTenant = (parameters, contractsById, tenantCompanyClusterId) => {
    if (!parameters || !parameters['ContractNumber']) return parameters;
    if (!tenantCompanyClusterId) return parameters; // unknown tenant -> don't hide anything

    const filteredContractNumbers = parameters['ContractNumber'].filter((parameterValue) => {
        if (parameterValue.id === 1) return true;
        const contract = contractsById ? contractsById[parameterValue.id] : null;
        return !contract || contract.tenantCompanyClusterId === tenantCompanyClusterId;
    });

    return { ...parameters, ContractNumber: filteredContractNumbers };
};

// Explicit currency -> display symbol map. Intl's fi-FI locale data doesn't have a "kr" symbol for SEK
// (it falls back to the ISO code "SEK" instead -- see doc/sweden-expansion-currency-plan.md), so we
// can't rely on Intl's currency style/currencyDisplay for every supported currency. Keep this in sync
// with any new tenant currency added to the system.
const CURRENCY_SYMBOLS = {
    EUR: '€',
    SEK: 'kr',
};

// currency defaults to 'EUR' only as a last resort (e.g. legacy pre-Sweden-expansion data with no
// tenant reference) -- callers that have a real entity/tenant currency available should always pass
// it explicitly (see common/currencyutils.js for resolving a tenantCompanyClusterId to a currency).
export const formatCurrency = (value, fractionDigits = 2, currency = 'EUR') => {
    const formatter = new Intl.NumberFormat('fi-FI', {
        style: 'decimal',
        minimumFractionDigits: fractionDigits,
    });
    return `${formatter.format(value)}\u00A0${currencySymbolFor(currency)}`;
};

// For spots that can't use the full formatCurrency() (e.g. an input adornment like "€/kpl" that
// needs just the symbol next to a raw numeric TextField) -- same currency-defaults-to-EUR rule.
export const currencySymbolFor = (currency = 'EUR') => {
    const code = currency || 'EUR';
    if (CURRENCY_SYMBOLS[code]) return CURRENCY_SYMBOLS[code];
    try {
        const parts = new Intl.NumberFormat('fi-FI', {
            style: 'currency',
            currency: code,
        }).formatToParts(0);
        const symbolPart = parts.find((p) => p.type === 'currency');
        return symbolPart ? symbolPart.value : code;
    } catch {
        return code;
    }
};

export const verifyAndReturnDecimal = (value, decimals) => {
    let valueStr = value.toString().replace(',', '.');
    if (!isNaN(parseFloat(valueStr))) {
        return parseFloat(valueStr);
    }
    return null;
};
